'use client';
import { useAppDispatch, useAppSelector } from '@/lib/hooks/storeHook';
import {
  __unsubscribeAll,
  reduxAllSubscribe,
  setSubscribe,
  SubscribeKeyType,
} from '@/lib/stores/subscribeSlice';
import { useEffect } from 'react';
import { useSession } from '@/lib/hooks/useSession';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import {
  CandleStickTickData,
  setCandleStickTickData,
} from '@/lib/stores/dataSlice';
import {
  reduxWebsocketState,
  setSendJsonMessage,
  setWebsocketState,
} from '@/lib/stores/stateSlice';
import { buildWebsocketRequest } from '@/lib/utils/requestBuilder.utils';
import { useQueryClient } from '@tanstack/react-query';
import { CandleStickItemWs } from '@/lib/services/candleStick/candleStick.service';
import { subscribeChannel } from '@/lib/config/channel';
import { updateWsToken, WsTokenState } from '@/lib/stores/wsTokenSlice';
import {
  updateWsSimpleToken,
  WsSimpleTokenState,
} from '@/lib/stores/wsSimpleTokenInfo';
import {
  updateWsTradeList,
  selectWsTradeList,
} from '@/lib/stores/wsTradeListSlice';
import {
  toaster,
  type OrderResultWsItem,
  OrderStatus,
} from '@/components/ui/orderResultToaster';
import { toaster as toasterTip } from '@/components/ui/toaster';
import {
  setRefreshBalance,
  setRefreshTradeHistory,
  setRefreshLimitOrder,
  setRefreshHoldingHistory,
} from '@/lib/stores/accountSlice';
import { handleTradeList } from '@/lib/utils/processData.utils';
import Decimal from 'decimal.js';

export enum WebsocketAction {
  SUBSCRIBE = 'subscribe',
  UNSUBSCRIBE = 'unsubscribe',
  PONG = 'pong',
  PING = 'ping',
  CONNECT = 'connect',
  MESSAGE = 'message',
  LOGIN = 'login',
  LOGOUT = 'logout',
}
type WebSocketMessage<T> = {
  id: string;
  code: number;
  message: string;
  action:
    | WebsocketAction.SUBSCRIBE
    | WebsocketAction.UNSUBSCRIBE
    | WebsocketAction.PING
    | WebsocketAction.CONNECT
    | WebsocketAction.MESSAGE
    | WebsocketAction.LOGIN
    | WebsocketAction.LOGOUT;
  channel: string;
  data: T;
};
const wsUrl = process.env.NEXT_PUBLIC_WS_URL;
let num = 0;
let count = 0;
let siv = null;
let timer = 3000;
export function useSocketData() {
  const rWebsocketState = useAppSelector(reduxWebsocketState);
  const dispatch = useAppDispatch();
  const rAllSubscribe = useAppSelector(reduxAllSubscribe);
  const rTradeList = useAppSelector(selectWsTradeList);
  const { data: sessionData } = useSession();
  const queryClient = useQueryClient();
  const { sendMessage, sendJsonMessage, readyState, getWebSocket } =
    useWebSocket(wsUrl ?? '', {
      shouldReconnect: (closeEvent) => {
        return true;
      },
      onOpen: (event) => {
        num = 0;
        count = 0;
        siv = setInterval(() => {
          count = count + 1;
          if (count >= 4) {
            getWebSocket()?.close();
            dispatch(
              setWebsocketState({
                ...rWebsocketState,
                isConnected: false,
              }),
            );
            dispatch(__unsubscribeAll({}));
          }
        }, 20000);
      },
      onClose: () => {
        num = num + 1;
        if (num <= 3) {
          timer = timer * num;
        } else {
          timer = 9000;
        }
        dispatch(
          setWebsocketState({
            ...rWebsocketState,
            isConnected: false,
          }),
        );
        dispatch(__unsubscribeAll({}));
      },
      onMessage: (message: MessageEvent) => {
        const parsedData = JSON.parse(
          message.data,
        ) as WebSocketMessage<unknown>;
        if (parsedData.action === WebsocketAction.PING) {
          count = 0;
          sendJsonMessage({ action: WebsocketAction.PONG });
          return;
        }
        if (
          parsedData.action === WebsocketAction.LOGOUT ||
          parsedData.action === WebsocketAction.LOGIN
        ) {
          return;
        }
        if (parsedData.action === WebsocketAction.CONNECT) {
          dispatch(
            setWebsocketState({
              ...rWebsocketState,
              isConnected: true,
              isFirstConnected: true,
            }),
          );
          return;
        }
        if (parsedData.action === WebsocketAction.MESSAGE) {
          switch (parsedData.channel) {
            case subscribeChannel.candleStickSubscribe:
              const candleData = parsedData.data as CandleStickItemWs | null;
              if (candleData) {
                const _candleData: CandleStickTickData = {
                  time: +candleData.candle_time * 1000,
                  open: candleData.open,
                  high: candleData.high,
                  low: candleData.low,
                  close: candleData.close,
                  volume: candleData.volume_token,
                };
                dispatch(setCandleStickTickData(_candleData));
              }
              return;
            case subscribeChannel.simpleTokenInfoSubscribe:
              const simpleTokenInfoData = parsedData.data as WsSimpleTokenState;
              dispatch(updateWsSimpleToken(simpleTokenInfoData));
              return;
            case subscribeChannel.tokenInfoSubscribe:
              const tokenInfoData = parsedData.data as WsTokenState;
              dispatch(updateWsToken(tokenInfoData));
              dispatch(
                updateWsTradeList(
                  handleTradeList(rTradeList, tokenInfoData.trades),
                ),
              );
              return;
            case subscribeChannel.orderResultSubscribe:
              const orderResultData = parsedData.data as OrderResultWsItem;
              dispatch(setRefreshLimitOrder(true));
              if (orderResultData.status === OrderStatus.Suc) {
                dispatch(setRefreshBalance(true));
                dispatch(setRefreshHoldingHistory(true));
                toaster.create({
                  orderInfo: {
                    isBuy: orderResultData.swap_type === 1,
                    finalCap: orderResultData.final_cap,
                    chainId: orderResultData.chain_id,
                    tokenSymbol: orderResultData.token_symbol,
                    orderAmount: orderResultData.order_amount,
                    finalAmount: orderResultData.final_amount,
                    status: orderResultData.status,
                  },
                });
              } else {
                toaster.create({
                  orderInfo: {
                    isBuy: orderResultData.swap_type === 1,
                    finalCap: orderResultData.order_cap,
                    chainId: orderResultData.chain_id,
                    tokenSymbol: orderResultData.token_symbol,
                    orderAmount: orderResultData.order_amount,
                    finalAmount:
                      orderResultData.swap_type === 1
                        ? new Decimal(orderResultData.order_value_base)
                            .div(orderResultData.order_price_base)
                            .toString()
                        : orderResultData.order_value_base,
                    status: orderResultData.status,
                  },
                });
              }
              if (
                orderResultData.status === OrderStatus.Suc ||
                orderResultData.status === OrderStatus.Fail
              ) {
                dispatch(setRefreshTradeHistory(true));
                queryClient.invalidateQueries({
                  queryKey: [
                    'infinite_order_history',
                    {
                      pageSize: 10,
                      tokenCa: orderResultData.token_ca,
                    },
                  ],
                });
                queryClient.invalidateQueries({
                  queryKey: [
                    'infinite_order_history',
                    {
                      pageSize: 10,
                    },
                  ],
                });
              }
              return;
          }
        }
      },
      reconnectAttempts: 9999999999,
      reconnectInterval: timer,
    });
  const connectionStatus = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  }[readyState];
  useEffect(() => {
    if (rWebsocketState.isConnected) {
      Object.entries(rAllSubscribe).forEach((key) => {
        let name = key[0];
        let item: any = key[1];
        // if (name == 'simpleTokenInfoSubscribe') {
        //   /**
        //    * store已经订阅,但是请求未发出,手动触发请求
        //    */
        //   if (item.subscribed && !item.__subscribing) {
        //     let keys = Object.keys(item.filter);
        //     keys.forEach((val) => {
        //       sendMessage(
        //         buildWebsocketRequest({
        //           action: WebsocketAction.SUBSCRIBE,
        //           channel: subscribeChannel[name as SubscribeKeyType],
        //           data: item.filter[val],
        //         }),
        //       );
        //     });
        //     dispatch(
        //       setSubscribe({
        //         type: name as SubscribeKeyType,
        //         data: {
        //           __subscribing: true,
        //         },
        //       }),
        //     );
        //   }
        //   /**
        //    * store已经取消订阅,但是请求依旧进行,手动取消订阅
        //    */
        //   if (!item.subscribed && item.__subscribing) {
        //     sendMessage(
        //       buildWebsocketRequest({
        //         action: WebsocketAction.UNSUBSCRIBE,
        //         channel: subscribeChannel[name as SubscribeKeyType],
        //         data: {},
        //       }),
        //     );
        //     dispatch(
        //       setSubscribe({
        //         type: name as SubscribeKeyType,
        //         data: {
        //           __subscribing: false,
        //         },
        //       }),
        //     );
        //   }
        // } else {
        /**
         * store已经订阅,但是请求未发出,手动触发请求
         */
        if (item.subscribed && !item.__subscribing) {
          sendMessage(
            buildWebsocketRequest({
              action: WebsocketAction.SUBSCRIBE,
              channel: subscribeChannel[name as SubscribeKeyType],
              data: rAllSubscribe[name as SubscribeKeyType].filter,
            }),
          );
          dispatch(
            setSubscribe({
              type: name as SubscribeKeyType,
              data: {
                __subscribing: true,
              },
            }),
          );
        }
        /**
         * store已经取消订阅,但是请求依旧进行,手动取消订阅
         */
        if (!item.subscribed && item.__subscribing) {
          sendMessage(
            buildWebsocketRequest({
              action: WebsocketAction.UNSUBSCRIBE,
              channel: subscribeChannel[name as SubscribeKeyType],
              data: rAllSubscribe[name as SubscribeKeyType].filter,
            }),
          );
          dispatch(
            setSubscribe({
              type: name as SubscribeKeyType,
              data: {
                __subscribing: false,
              },
            }),
          );
        }
        // }
      });
    }
  }, [dispatch, rAllSubscribe, rWebsocketState.isConnected, sendMessage]);

  useEffect(() => {
    if (rWebsocketState.isConnected) {
      if (sessionData?.isLogin) {
        sendMessage(
          buildWebsocketRequest({
            action: WebsocketAction.LOGIN,
            channel: '',
            data: {
              token: sessionData.token,
            },
          }),
        );
      }
    }
    return () => {
      sendMessage(
        buildWebsocketRequest({
          action: WebsocketAction.LOGOUT,
          channel: '',
          data: {},
        }),
      );
    };
  }, [sessionData, rWebsocketState.isConnected]);

  useEffect(() => {
    dispatch(setSendJsonMessage(sendJsonMessage));
  }, [dispatch, sendJsonMessage]);
}
